import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';

const NAV_LINKS = [
  { to: '/mice', key: 'nav.mice' },
  { to: '/events', key: 'nav.events' },
  { to: '/weddings', key: 'nav.weddings' },
  { to: '/portfolio', key: 'nav.portfolio' },
  { to: '/journal', key: 'nav.journal' },
  { to: '/studio', key: 'nav.studio' },
  { to: '/faq', key: 'nav.faq' },
];

const headingStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const Footer = () => {
  const t = useT();

  return (
    <footer
      className="text-bone mt-20 overflow-hidden"
      style={{ backgroundColor: '#060B22', borderTop: '1px solid rgba(214,168,122,0.35)' }}
      data-testid="footer"
    >
      {/* ───── Finale, the editorial ending ───── */}
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
        <div style={headingStyle} data-testid="footer-finale-eyebrow">
          GlowMeets, {t('footer.officesLine')}
        </div>

        <h2
          className="mt-8"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: 'clamp(40px, 6vw, 88px)',
            lineHeight: 1.02,
            fontWeight: 500,
            color: '#F4EFE3',
            maxWidth: '18ch',
          }}
          data-testid="footer-statement"
        >
          {t('footer.statement1')}{' '}
          <em className="italic" style={{ color: '#D6A87A', fontWeight: 400 }}>
            {t('footer.statement2')}
          </em>
        </h2>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12pt',
              color: 'rgba(244,239,227,0.72)',
            }}
          >
            {t('footer.beginLine')}
          </p>
          <Link
            to="/contact"
            data-testid="footer-begin-link"
            className="group inline-flex items-baseline gap-2"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '11pt',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#D6A87A',
            }}
          >
            <span
              style={{ borderBottom: '1px solid #D6A87A', paddingBottom: '3px' }}
              className="transition-colors duration-200 group-hover:text-bone"
            >
              {t('footer.begin')}
            </span>
            <span aria-hidden="true" className="rtl:-scale-x-100 inline-block">→</span>
          </Link>
        </div>
      </div>

      {/* hairline */}
      <div
        className="max-w-container mx-auto px-4 sm:px-6 lg:px-8"
        aria-hidden="true"
      >
        <div style={{ borderTop: '1px solid rgba(214,168,122,0.22)' }} />
      </div>

      {/* ───── Columns ───── */}
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <img
              src="/glowmeets-wordmark-mice.png"
              alt="GlowMeets, MICE, a Mervin Travel brand"
              className="h-20 w-auto mb-4"
              style={{ display: 'block' }}
            />
            <a
              href="https://www.mervintravel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="by-mervin-travel shimmer inline-block"
            >
              by Mervin Travel
            </a>
            <p
              className="mt-5 max-w-sm"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                lineHeight: 1.7,
                color: 'rgba(244,239,227,0.62)',
              }}
              data-testid="footer-tagline"
            >
              {t('footer.tagline')}
            </p>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h4 style={headingStyle} className="mb-5" data-testid="footer-explore-heading">
              {t('footer.explore')}
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="transition-colors duration-200 hover:text-copper"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '11pt',
                      color: 'rgba(244,239,227,0.72)',
                    }}
                  >
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices */}
          <div className="md:col-span-3">
            <h4 style={headingStyle} className="mb-5" data-testid="footer-offices-heading">
              {t('footer.ourOffices')}
            </h4>
            <p
              className="text-copper mb-4"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 500,
                letterSpacing: '0.08em',
              }}
              data-testid="footer-offices-line"
            >
              {t('footer.officesLine')}
            </p>
            <div className="flex items-start gap-3">
              <MapPin size={16} className="text-copper mt-1 flex-shrink-0" />
              <span
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '10pt',
                  lineHeight: 1.7,
                  color: 'rgba(244,239,227,0.62)',
                }}
              >
                Gursel Mahallesi Imrahor Caddesi No.29 B Premier Campus Plaza
                Suite 437, Istanbul-Turkiye
              </span>
            </div>
          </div>

          {/* Direct */}
          <div className="md:col-span-2">
            <h4 style={headingStyle} className="mb-5" data-testid="footer-direct-heading">
              {t('footer.directHeading')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-copper flex-shrink-0" />
                <a
                  href="mailto:mice@glowmeets.com"
                  className="transition-colors duration-200 hover:text-copper"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '10pt',
                    color: 'rgba(244,239,227,0.72)',
                  }}
                >
                  mice@glowmeets.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-copper flex-shrink-0" />
                <a
                  href="tel:+905441181827"
                  className="transition-colors duration-200 hover:text-copper"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '10pt',
                    color: 'rgba(244,239,227,0.72)',
                  }}
                >
                  +90 544 118 1827
                </a>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a href="#" className="text-bone/60 hover:text-copper transition-colors" aria-label="LinkedIn">
                <Linkedin size={18} />
              </a>
              <a href="#" className="text-bone/60 hover:text-copper transition-colors" aria-label="Twitter">
                <Twitter size={18} />
              </a>
              <a href="#" className="text-bone/60 hover:text-copper transition-colors" aria-label="Facebook">
                <Facebook size={18} />
              </a>
              <a href="#" className="text-bone/60 hover:text-copper transition-colors" aria-label="Instagram">
                <Instagram size={18} />
              </a>
            </div>
            <div className="mt-6">
              <LanguageToggle variant="footer" />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-14 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ borderTop: '1px solid rgba(214,168,122,0.22)' }}
        >
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '9pt',
              letterSpacing: '0.06em',
              color: 'rgba(244,239,227,0.5)',
            }}
            data-testid="footer-copyright"
          >
            © {new Date().getFullYear()} GlowMeets. {t('footer.rights')}
          </p>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '9pt',
              letterSpacing: '0.06em',
              color: 'rgba(244,239,227,0.5)',
            }}
            data-testid="footer-tursab"
          >
            {t('common.tursab')}
          </p>
        </div>
      </div>

      {/* ───── Watermark wordmark, the very last thing the eye reads ───── */}
      <div
        aria-hidden="true"
        className="relative select-none pointer-events-none"
        style={{ height: 'clamp(70px, 9vw, 130px)', overflow: 'hidden' }}
        data-testid="footer-watermark"
      >
        <div
          className="absolute inset-x-0 text-center whitespace-nowrap"
          style={{
            top: 0,
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: 'clamp(120px, 17vw, 250px)',
            lineHeight: 0.78,
            color: 'rgba(214,168,122,0.08)',
            letterSpacing: '0.02em',
          }}
        >
          GlowMeets
        </div>
      </div>
    </footer>
  );
};

export default Footer;
